/** Shared task checklist definition editor for templates and work orders. */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { Schemas } from "@/api/client";
import { localId, SortableList } from "@/shared/components/sortable-list";
import { Button } from "@/shared/ui/button";
import { Checkbox, Input, Select } from "@/shared/ui/form";

/** Editable checklist row, preserving server IDs and constraints. */
export type ChecklistRow = Schemas["ChecklistItemIn"] & {
  _id: string;
  id?: string | null;
  _options?: string;
};

const RESPONSE_TYPES = [
  "pass_fail",
  "yes_no",
  "number",
  "text",
  "choice",
  "photo",
  "signature",
  "reading",
] as const;

/** Edit, append, remove and reorder checklist questions before saving a task. */
export function ChecklistEditor({
  items,
  onChange,
  taskIndex,
}: {
  items: ChecklistRow[];
  onChange: (items: ChecklistRow[]) => void;
  taskIndex: number;
}): ReactElement {
  const { t } = useTranslation();
  const update = (id: string, patch: Partial<ChecklistRow>): void =>
    onChange(items.map((item) => (item._id === id ? { ...item, ...patch } : item)));
  return (
    <div className="mt-2 space-y-2 border-t pt-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("workOrders.checklist")}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            onChange([
              ...items,
              {
                _id: localId(),
                prompt: "",
                response_type: "pass_fail",
                constraints: {},
                is_mandatory: true,
                photo_required: false,
                _options: "",
              },
            ])
          }
        >
          {t("jobTemplates.addCheck")}
        </Button>
      </div>
      <SortableList
        items={items}
        getId={(item) => item._id}
        onChange={onChange}
        renderItem={(item, index) => (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              aria-label={`${t("jobTemplates.prompt")} ${taskIndex + 1}.${index + 1}`}
              placeholder={t("jobTemplates.prompt")}
              value={item.prompt}
              maxLength={300}
              required
              onChange={(event) => update(item._id, { prompt: event.target.value })}
            />
            <Select
              aria-label={`${t("jobTemplates.responseType")} ${taskIndex + 1}.${index + 1}`}
              value={item.response_type}
              onChange={(event) => update(item._id, { response_type: event.target.value })}
            >
              {RESPONSE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`jobTemplates.responseTypes.${type}`)}
                </option>
              ))}
            </Select>
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
              <Checkbox
                id={`check-${item._id}-mand`}
                label={t("app.required")}
                checked={item.is_mandatory}
                onChange={(event) => update(item._id, { is_mandatory: event.target.checked })}
              />
              <Checkbox
                id={`check-${item._id}-photo`}
                label={t("jobTemplates.photo")}
                checked={item.photo_required}
                onChange={(event) => update(item._id, { photo_required: event.target.checked })}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label={`${t("app.delete")} ${taskIndex + 1}.${index + 1}`}
                onClick={() => onChange(items.filter((row) => row._id !== item._id))}
              >
                ×
              </Button>
            </div>
            {item.response_type === "choice" ? (
              <Input
                className="sm:col-span-2"
                aria-label={`${t("jobTemplates.choiceOptions")} ${taskIndex + 1}.${index + 1}`}
                placeholder={t("jobTemplates.choiceOptionsHint")}
                value={item._options ?? ""}
                required
                onChange={(event) => update(item._id, { _options: event.target.value })}
              />
            ) : null}
          </div>
        )}
      />
    </div>
  );
}
